/**
 * Customized for HDM header
 */

import { Linkable } from '@/components/blocks/Linkable';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import {
	BASE_ROUTE_HOME,
	ESPOT_MENUS,
	HEADER_ADOBE_TARGET_VIEW,
	HEADER_ESPOT_LOWER_SECTION,
	HEADER_ESPOT_SUPERIOR_SECTION,
	HEADER_ID,
	MAIN_HEADER_LOGO_ESPOT,
} from '@/components/content/Header/constants';
import { HeaderPanelsProvider } from '@/components/content/Header/context/HeaderPanelsContext';
import { HeaderAccount } from '@/components/content/Header/parts/Account';
import { HeaderDeliveryLocator } from '@/components/content/Header/parts/DeliveryLocator';
import { DepartmentsOptionHeader } from '@/components/content/Header/parts/DepartmentsOption';
import { EmarketingSpotMenu } from '@/components/content/Header/parts/EmarketingSpotMenu';
import { HeaderLocateStore } from '@/components/content/Header/parts/LocateStore';
import { HeaderMiniCart } from '@/components/content/Header/parts/MiniCart';
import { HeaderMobileDrawer } from '@/components/content/Header/parts/MobileDrawer';
import { MyRecentOrdersHeader } from '@/components/content/Header/parts/MyRecentOrders';
import { MyWishListHeader } from '@/components/content/Header/parts/MyWishList';
import { HeaderSearch } from '@/components/content/Header/parts/Search';
import { StickyMobileBottomHeader } from '@/components/content/Header/parts/StickyMobileBottomHeader';
import {
	headerContainerComponentSX,
	headerContainerSX,
	headerSectionButtonContainerSX,
	headerStoreSectionContainerSX,
	headerStoreSectionGrowContainer,
	headerStoreSectionNoGrowContainer,
	headerStoreSectionStackCDividerSX,
	headerStoreSectionStackContainerSX,
} from '@/components/content/Header/styles/container';
import { headerLogoContainerSX } from '@/components/content/Header/styles/logoContainer';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useEmarsysTag } from '@/data/Content/EmarsysTag';
import { useSettings } from '@/data/Settings';
import { ID } from '@/data/types/Basic';
import { mobileDisplaySx } from '@/styles/mobileApp';
import { Box, Divider, Grid, Paper, Stack, useMediaQuery, useTheme } from '@mui/material';
import Head from 'next/head';
import { useAdobeTargetComponentView } from 'presentation/events/hooks/adobe/useAdobeTargetComponentView';
import { FC, useCallback, useState } from 'react';
import { ENABLE_SEO_LINKS_HOMEPAGE } from 'shared/custom/constants/commonConstants';
import { HOME } from 'shared/custom/constants/routes';
export const Header: FC<{
	id: ID;
}> = ({ id }) => {
	useEmarsysTag();
	useAdobeTargetComponentView(HEADER_ADOBE_TARGET_VIEW);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const router = useNextRouter();

	const toggleDrawer = useCallback(
		(open?: boolean) => () => {
			setDrawerOpen(open !== undefined ? open : (current) => !current);
		},
		[]
	);

	const theme = useTheme();
	const isMobileAndTablet = useMediaQuery(theme.breakpoints.down('lg'));
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { settings } = useSettings();
	const isMobileApp = settings.isMobileApp;

	return (
		<HeaderPanelsProvider>
			<Head>
				{settings.userData?.[ENABLE_SEO_LINKS_HOMEPAGE] === 'true' && router.pathname === HOME && (
					<>
						<link rel="alternate" href="https://www.homedepot.com/" hrefLang="en-us" />
						<link rel="alternate" href="https://www.homedepot.ca/en/home.html/" hrefLang="en-ca" />
						<link
							rel="alternate"
							href="https://www.homedepot.ca/fr/accueil.html"
							hrefLang="fr-ca"
						/>
						<link rel="alternate" href="https://www.homedepot.com.mx/" hrefLang="es-mx" />
					</>
				)}
			</Head>
			<Grid item xs={12} md={12} sx={mobileDisplaySx(isMobileApp)}>
				<EMarketingSpot
					id={HEADER_ESPOT_SUPERIOR_SECTION}
					properties={{ emsName: HEADER_ESPOT_SUPERIOR_SECTION }}
				/>
			</Grid>
			<Paper
				component="header"
				sx={{ ...headerContainerSX(theme), ...mobileDisplaySx(isMobileApp) }}
				elevation={1}
				id={HEADER_ID}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					spacing={{ xs: 1.25, sm: 1.25, md: 2, lg: 2 }}
					py={2}
					sx={headerContainerComponentSX}
				>
					<Stack
						direction="row"
						alignItems="center"
						spacing={2}
						sx={headerStoreSectionNoGrowContainer}
					>
						{/* <HeaderMobileToggleButton toggleDrawer={toggleDrawer} open={drawerOpen} />*/}
						<HeaderMobileDrawer toggleDrawer={toggleDrawer} open={drawerOpen} />

						<Box sx={headerLogoContainerSX(isMobileAndTablet)} id="logo">
							<Linkable href={BASE_ROUTE_HOME} id={`${MAIN_HEADER_LOGO_ESPOT}-link`}>
								<EMarketingSpot
									id={MAIN_HEADER_LOGO_ESPOT}
									properties={{ emsName: MAIN_HEADER_LOGO_ESPOT }}
								/>
							</Linkable>
						</Box>
						{!isMobileAndTablet ? (
							<>
								<DepartmentsOptionHeader />
								{ESPOT_MENUS.map(({ trigger, title, items }) => (
									<EmarketingSpotMenu
										key={trigger}
										menuDynamicTriggerEspot={trigger}
										menuDynamicTitleEspot={title}
										menuDynamicsItemsEspot={items}
									/>
								))}
							</>
						) : null}
					</Stack>
					<Stack
						direction="row"
						alignItems="flex-end"
						justifyContent="flex-end"
						spacing={2}
						sx={headerStoreSectionGrowContainer}
					>
						<HeaderSearch />
					</Stack>
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="flex-end"
						spacing={{ xs: 1.25, sm: 1.25, md: 2, lg: 2 }}
						sx={headerStoreSectionNoGrowContainer}
					>
						{!isMobileAndTablet ? (
							<>
								<HeaderAccount mobileBreakpoint="md" />
								<MyRecentOrdersHeader />
							</>
						) : null}
						<MyWishListHeader />
						<HeaderMiniCart mobileBreakpoint="lg" />
						{/* <HeaderLanguageAndCurrency /> */}
					</Stack>
				</Stack>
			</Paper>
			<Paper
				sx={{
					...headerStoreSectionContainerSX,
					...mobileDisplaySx(isMobileApp),
				}}
			>
				<Stack sx={headerStoreSectionStackContainerSX} direction="row">
					<Box sx={headerSectionButtonContainerSX(true)}>
						<HeaderLocateStore />
					</Box>
					<Divider orientation="vertical" sx={headerStoreSectionStackCDividerSX} />
					<Box sx={headerSectionButtonContainerSX(false)}>
						<HeaderDeliveryLocator />
					</Box>
				</Stack>
			</Paper>
			<Grid item xs={12} md={12} sx={mobileDisplaySx(isMobileApp)}>
				<EMarketingSpot
					id={HEADER_ESPOT_LOWER_SECTION}
					properties={{ emsName: HEADER_ESPOT_LOWER_SECTION }}
				/>
			</Grid>
			{isMobileAndTablet ? <StickyMobileBottomHeader /> : null}
		</HeaderPanelsProvider>
	);
};
